/**
 * @fileoverview The org Dashboard's Lessons tab: every session across every
 * program, in the rows a program's own Sessions tab prints (SessionList).
 *
 * Read from `GET /org/lessons` when the tab first opens — the program rows
 * plus any org lesson no program row covers, so the list is complete against
 * the Overview's sessions taught. It opens on the dashboard's own range (the
 * `window` period), and asked for upcoming sessions, on the Upcoming tile's
 * next 30 days (the `upcoming` period, the window the server counted), so the
 * tile a director clicked and the rows under it cannot disagree; the ordinary
 * periods follow for anything else.
 *
 * The kind and status choices live in the URL filter, like the program tab,
 * so the Upcoming tile can open this list on exactly the rows it counted.
 *
 * Every session can be ticked (useOrgSessionActions): the missing-notes
 * notices among them cleared in bulk from every program at once, the ticked
 * rows exported, archived (off the list, still counted, under the Archived
 * status) or deleted (off the list and out of the numbers). "Missing notes"
 * in the status filter, then Select all, is the quick way to a term's backlog.
 *
 * Laid out to start the list straight under the tabs: one row of dropdowns
 * (the dashboard's Export sits in the tab row above), the count in the list's
 * own bar, and the first 25 sessions of the range — its earliest at the top —
 * with Show more under them (MoreRows).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import orgApi from "@/lib/orgApi";
import LessonFilterBar from "@/components/studio/LessonFilterBar";
import SessionList from "@/components/org/SessionList";
import { MoreRowsBar, usePagedRows } from "@/components/studio/MoreRows";
import useOrgSessionActions, { ArchivedNote } from "@/components/org/useOrgSessionActions";
import { filterLessons, PERIODS, sortChronologically } from "@/lib/lessonHistory";
import {
  SESSION_KINDS,
  SESSION_STATUSES,
  STATUS_FILTERS,
  UPCOMING_PERIOD,
  WINDOW_PERIOD,
  byStatus,
  distinctOptions,
  inWindow,
  sessionRow,
  statusFromFilter,
} from "@/lib/orgSessions";

/**
 * @param {{
 *   range: {label: string, start: string, end: string}|null,
 *   filter: string|null,
 *   onFilter: (filter: string|null) => void,
 * }} props
 */
export default function OrgLessonsTab({ range, filter, onFilter }) {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [bar, setBar] = useState(() => ({
    // Upcoming sessions are ahead of today, the dashboard's range behind it.
    period: statusFromFilter(filter) === "upcoming" ? UPCOMING_PERIOD : WINDOW_PERIOD,
    instructor: null,
    classId: null,
    program: null,
    search: "",
  }));

  const load = useCallback(
    () =>
      orgApi
        .get("/org/lessons")
        .then((res) => res.data)
        .catch(() => ({ sessions: [], failed: true })),
    [],
  );

  useEffect(() => {
    let live = true;
    load().then((data) => live && setPayload(data));
    return () => {
      live = false;
    };
  }, [load]);

  const rows = useMemo(() => (payload?.sessions || []).map(sessionRow), [payload]);
  const kind = filter === "solo" ? "student" : filter === "class" ? "class" : "all";
  const status = statusFromFilter(filter);

  /* A tile's own window is a period here: the dashboard's range behind, the
     Upcoming tile's 30 days ahead. */
  const tilePeriod = bar.period === WINDOW_PERIOD || bar.period === UPCOMING_PERIOD;
  const tileWindow = bar.period === WINDOW_PERIOD ? range : bar.period === UPCOMING_PERIOD ? payload?.upcoming_window : null;
  const shown = useMemo(() => {
    const scoped = byStatus(tilePeriod ? inWindow(rows, tileWindow) : rows, status);
    return sortChronologically(
      filterLessons(
        scoped.rows,
        { ...bar, period: tilePeriod ? "all" : bar.period, kind, status: scoped.status },
        (r) => `${r.class_name || ""} ${r.program_title || ""}`,
      ),
    );
  }, [rows, tilePeriod, tileWindow, bar, kind, status]);

  /* The first 25 (or 50, or 100) of the range, oldest at the top — the
     earliest session of the past 30 days opens the list — with the later ones
     a click below (MoreRows). Select all still reaches every filtered row. */
  const paged = usePagedRows(
    shown,
    [bar.period, bar.instructor, bar.classId, bar.program, bar.search, kind, status, tileWindow?.start, tileWindow?.end].join("|"),
  );

  const clearing = useOrgSessionActions({
    rows: shown,
    onChanged: () => load().then(setPayload),
    testId: "dashboard-notes-clear",
    summary:
      payload && !payload.failed ? (
        <span data-testid="dashboard-lessons-count">
          {shown.length.toLocaleString()} of {rows.length.toLocaleString()} sessions
        </span>
      ) : null,
    exportName: "hans-lessons-selected",
  });

  const programs = useMemo(() => {
    const listed = (payload?.programs || []).map((p) => ({ id: p.program_id, name: p.title }));
    return listed.length ? listed : distinctOptions(rows, (r) => r.program_id, (r) => r.program_title);
  }, [payload, rows]);
  const instructors = useMemo(() => distinctOptions(rows, (r) => r.user_id, (r) => r.instructor_name), [rows]);
  const classes = useMemo(
    () => distinctOptions(bar.program ? rows.filter((r) => r.program_id === bar.program) : rows, (r) => r.class_id, (r) => r.class_name),
    [rows, bar.program],
  );

  return (
    <>
      <LessonFilterBar
        value={{ ...bar, kind, status }}
        onChange={(next) => {
          const kindFilter = next.kind === "student" ? "solo" : next.kind === "class" ? "class" : null;
          const statusFilter = STATUS_FILTERS[next.status] || null;
          const wanted = next.kind !== kind ? kindFilter : next.status !== status ? statusFilter : filter || null;
          if (wanted !== (filter || null)) onFilter(wanted);
          // A class belongs to one program: choosing another program drops it.
          const classId = next.program !== bar.program ? null : next.classId;
          // The dashboard's range is behind today, where nothing is upcoming:
          // asking for upcoming sessions looks ahead instead, over the tile's window.
          const period =
            next.status === "upcoming" && status !== "upcoming" && next.period === WINDOW_PERIOD ? UPCOMING_PERIOD : next.period;
          setBar({ period, instructor: next.instructor, classId, program: next.program, search: next.search });
        }}
        periods={[
          { id: WINDOW_PERIOD, label: range?.label || "Past 30 days" },
          { id: UPCOMING_PERIOD, label: payload?.upcoming_window?.label || "Next 30 days" },
          ...PERIODS,
        ]}
        kinds={SESSION_KINDS}
        statuses={SESSION_STATUSES}
        programs={programs}
        instructors={instructors}
        classes={classes}
        unit="sessions"
        searchPlaceholder="Search lessons"
      />
      {status === "archived" && <ArchivedNote testId="dashboard-archived-note" />}
      <SessionList
        loaded={Boolean(payload)}
        failed={payload?.failed}
        total={rows.length}
        rows={paged.shown}
        showProgram
        onOpenClass={(classId) => navigate(`/org/classes/${classId}`)}
        emptyText="Nothing has been scheduled or taught in your programs yet."
        testId="dashboard-lessons"
        rowTestId="dashboard-lesson"
        className="mb-3"
        header={clearing.toolbar}
        leadingFor={clearing.checkbox}
      />
      {payload && !payload.failed && <MoreRowsBar paged={paged} unit="session" testId="dashboard-lessons-more" className="mb-4" />}
      {clearing.dialogs}
      <p className="max-w-3xl font-sans text-[12px] leading-relaxed text-[var(--text-tertiary)]">
        Every program&rsquo;s sessions in one list, the way each program&rsquo;s Sessions tab shows them: date, class,
        who taught it and whether it was written up. What was said in a lesson is never listed here.
      </p>
    </>
  );
}
